import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import { store } from "../store";

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user?.token);
  const socket = useMemo(
    () =>
      io(process.env.REACT_APP_BACKEND_URL, {
        transports: ['websocket'],
        // The server rejects any handshake without a token, so connecting
        // without one just spins the reconnection loop forever on every
        // logged-out page. Connect only once a token is actually available
        // (see the token effect below).
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
        // A function (not a static value) so a refreshed/renewed access token is
        // picked up on every reconnect attempt, not just the token at mount time.
        auth: (cb) => cb({ token: store.getState().user?.token }),
      }),
    []
  );

  useEffect(() => {
    // Open the connection once logged in, and tear it down on logout -- also
    // closes the window where a stale/revoked token would otherwise keep an
    // already-open socket alive indefinitely.
    if (token) {
      if (!socket.connected) socket.connect();
    } else {
      socket.disconnect();
    }
  }, [token, socket]);

  useEffect(() => {
    // Listen for the 'userOnline' socket event and dispatch a Redux action
    socket.on("userOnline", (userId) => {
      console.log("userOnline event received with userId:", userId);
      dispatch({ type: "USER_ONLINE", payload: { userId } });
    });

    // Listen for the 'userOffline' socket event and dispatch a Redux action
    socket.on("userOffline", (userId) => {
      console.log("userOffline event received with userId:", userId);
      dispatch({ type: "USER_OFFLINE", payload: { userId } });
    });

    // Cleanup on unmount
    return () => {
      socket.off("userOnline");
      socket.off("userOffline");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};
